import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Header from './Header';
import Footer from './Footer';
import '../styles.css';
import { Connection, PublicKey } from '@solana/web3.js';
import { getAssociatedTokenAddress } from '@solana/spl-token';

const TransactionDetail = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [manualTxHashes, setManualTxHashes] = useState({}); // Store transaction hashes for manual confirmation
  const [showManualInputs, setShowManualInputs] = useState({}); // Track which transactions show the manual input field
  const [hideCheckSignature, setHideCheckSignature] = useState({}); // Track which transactions should hide the "Check Signature" button
  const [solPrice, setSolPrice] = useState(null); // Store SOL/USD price

  const navigate = useNavigate();

  const connection = useMemo(() => new Connection(
    process.env.REACT_APP_CHAINSTACK_API_URL || 'https://solana-mainnet.core.chainstack.com/8f1961548a0fd2824b4cbce7bfac5f46',
    'finalized'
  ), []); // Empty dependency array since the URL and commitment level are static

  const PRESALE_WALLET = '4mZ9yFABv79h9UNWR3TDSRqegj2Dzi3yZ74DjdySfusc';
  const USDC_MINT = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v';
  const USDT_MINT = 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB';

  // Fetch SOL/USD price from CoinGecko API
  const fetchSolPrice = useCallback(async () => {
    try {
      const response = await axios.get('https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd');
      const price = response.data.solana.usd;
      setSolPrice(price);
      return price;
    } catch (err) {
      console.error('Error fetching SOL price:', err);
      setError('Failed to fetch SOL price. Using default value.');
      const defaultPrice = 150; // Fallback price in case API fails
      setSolPrice(defaultPrice);
      return defaultPrice;
    }
  }, []);

  const fetchTransactions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Decode token (still needed for potential future use)
      jwtDecode(token);

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/user/purchases`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTransactions(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Fetch transactions error:', err);
      setError('Error fetching transactions: ' + (err.response ? err.response.data.error : err.message));
      setLoading(false);
      if (err.response?.status === 401) {
        navigate('/login');
      }
    }
  }, [navigate]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const checkTransaction = useCallback(async (transaction, txHash = null, useManualHash = false) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Please login first');
        navigate('/login');
        return { success: false };
      }

      const senderWallet = transaction.wallet_address;
      if (!senderWallet) {
        setError(`Sender wallet not found for purchase ${transaction.purchase_id}`);
        return { success: false };
      }

      let senderATA, receiverATA, selectedMint;
      // Initially assume the payment is USDC/USDT based on the selected paymentCoin
      selectedMint = transaction.payment_coin === 'USDC' ? USDC_MINT : USDT_MINT;
      senderATA = await getAssociatedTokenAddress(new PublicKey(selectedMint), new PublicKey(senderWallet));
      receiverATA = await getAssociatedTokenAddress(new PublicKey(selectedMint), new PublicKey(PRESALE_WALLET));
      if (!senderATA || !receiverATA) {
        setError(`Failed to compute ATA for sender or receiver for purchase ${transaction.purchase_id}`);
        return { success: false };
      }

      let signatures = [];
      let txHashResult = null;
      let amountPaid = 0;
      let actualSender = null;
      let paymentFound = false;
      let actualPaymentCoin = transaction.payment_coin; // Start with the selected payment coin

      if (useManualHash && txHash) {
        // Manual confirmation with transaction hash
        const hashCheck = await axios.get(`${process.env.REACT_APP_API_URL}/api/payment/check-transaction-hash/${txHash}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (hashCheck.data.isUsed) {
          console.log(`Transaction hash ${txHash} already used for another payment. Skipping.`);
          setError('This transaction hash has already been used. Please provide a different one.');
          return { success: false };
        }
        signatures = [{ signature: txHash, blockTime: null }];
      } else {
        // Automated confirmation
        console.log(`Fetching signatures for wallet ${senderWallet}`);
        const signaturesRes = await connection.getSignaturesForAddress(new PublicKey(senderWallet), { limit: 20 });
        signatures = signaturesRes.slice().reverse();
        console.log(`Found ${signatures.length} signatures for wallet ${senderWallet}`);
      }

      const purchaseCreatedAt = new Date(transaction.created_at).getTime() / 1000;

      // Fetch SOL price at the time of confirmation
      let currentSolPrice = solPrice;
      if (!currentSolPrice) {
        currentSolPrice = await fetchSolPrice();
      }

      for (const sig of signatures) {
        if (!useManualHash) {
          // Filter transactions by timestamp for automated checks
          if (sig.blockTime < purchaseCreatedAt) {
            console.log(`Skipping transaction ${sig.signature} as it occurred before purchase creation (${sig.blockTime} < ${purchaseCreatedAt})`);
            continue;
          }

          const hashCheck = await axios.get(`${process.env.REACT_APP_API_URL}/api/payment/check-transaction-hash/${sig.signature}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (hashCheck.data.isUsed) {
            console.log(`Transaction hash ${sig.signature} already used. Skipping.`);
            continue;
          }
        }

        console.log(`Fetching transaction details for ${sig.signature}`);
        const transactionData = await connection.getParsedTransaction(sig.signature, { 
          commitment: 'finalized',
          maxSupportedTransactionVersion: 0 // Add this to support version 0 transactions
        });
        if (!transactionData || !transactionData.meta) {
          console.log(`Transaction ${sig.signature} not found or invalid metadata`);
          continue;
        }

        const { meta, transaction: tx } = transactionData;
        if (!meta || !tx) {
          console.log(`Transaction ${sig.signature} missing required data`);
          continue;
        }

        const accountKeys = tx.message.accountKeys.map(key => key.pubkey.toBase58());
        // Use signatures to determine the sender (first signature corresponds to the primary signer)
        const signaturesList = tx.signatures;
        if (!signaturesList || signaturesList.length === 0) {
          console.log(`Transaction ${sig.signature} has no signatures`);
          continue;
        }
        // The sender is typically the first signer
        actualSender = signaturesList[0] ? accountKeys[0] : null;
        if (actualSender !== senderWallet) {
          console.log(`Sender mismatch in transaction ${sig.signature}. Expected: ${senderWallet}, Got: ${actualSender}`);
          continue;
        }

        // Check if this is a native SOL transfer
        const isNativeTransfer = meta.logMessages.some(log => log.includes('Program 11111111111111111111111111111111 invoke')) &&
                                 !meta.postTokenBalances.length &&
                                 !meta.preTokenBalances.length &&
                                 !meta.innerInstructions?.some(inner => inner.instructions.some(instr => instr.parsed?.type === 'transfer'));
        if (isNativeTransfer) {
          console.log(`Detected SOL transfer in transaction ${sig.signature}`);
          actualPaymentCoin = 'SOL';

          // Calculate SOL amount transferred to PRESALE_WALLET
          const senderPreBalance = meta.preBalances[0] || 0; // First account is the sender
          const senderPostBalance = meta.postBalances[0] || 0;
          const receiverIndex = accountKeys.findIndex(key => key === PRESALE_WALLET);
          const receiverPreBalance = receiverIndex !== -1 ? meta.preBalances[receiverIndex] || 0 : 0;
          const receiverPostBalance = receiverIndex !== -1 ? meta.postBalances[receiverIndex] || 0 : 0;

          // Amount received by PRESALE_WALLET (in lamports)
          const amountReceivedLamports = receiverIndex !== -1 ? receiverPostBalance - receiverPreBalance : 0;
          let solAmountPaid = amountReceivedLamports / 1_000_000_000; // Convert lamports to SOL

          // Deduct transaction fees from sender's balance change to estimate the amount paid
          const fee = meta.fee || 0; // Fee in lamports
          const senderBalanceChange = (senderPreBalance - senderPostBalance - fee) / 1_000_000_000; // Convert to SOL
          if (solAmountPaid <= 0 || senderBalanceChange <= 0) {
            console.log(`No valid SOL transfer amount found in transaction ${sig.signature}`);
            continue;
          }

          // Convert SOL amount to USD using the current SOL price
          amountPaid = solAmountPaid * currentSolPrice;
          console.log(`SOL transfer found in transaction ${sig.signature}: ${amountPaid} USD (${solAmountPaid} SOL at ${currentSolPrice} USD/SOL)`);

          // Special logic for SOL payments to handle volatility
          const expectedAmount = parseFloat(transaction.amount);
          const percentageDifference = (amountPaid - expectedAmount) / amountPaid; // Positive if user amount is lower, negative if higher
          if (percentageDifference > 0 && percentageDifference <= 0.25) {
            // User-entered amount is lower than actual market amount by 25% or less
            console.log(`User amount (${expectedAmount} USD) is ${percentageDifference * 100}% lower than market amount (${amountPaid} USD). Accepting user amount.`);
            amountPaid = expectedAmount;
          } else if (percentageDifference < 0) {
            // User-entered amount is higher than actual market amount
            console.log(`User amount (${expectedAmount} USD) is higher than market amount (${amountPaid} USD). Accepting market amount.`);
            // amountPaid remains as the market amount
          }
        } else {
          // Handle USDC/USDT payments (existing logic)
          const isSenderMatch = accountKeys.some(key => key === senderATA.toBase58());
          const isReceiverMatch = accountKeys.some(key => key === receiverATA.toBase58());
          if (!isSenderMatch || !isReceiverMatch) {
            console.log(`Sender or receiver mismatch in transaction ${sig.signature}. Sender ATA: ${senderATA.toBase58()}, Receiver ATA: ${receiverATA.toBase58()}`);
            continue;
          }

          amountPaid = 0;

          // Check top-level instructions for Transfer or TransferChecked
          if (tx.message.instructions) {
            for (const instr of tx.message.instructions) {
              if (instr.parsed && (instr.parsed.type === 'transfer' || instr.parsed.type === 'transferChecked') && 
                  instr.programId.toBase58() === 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA' && 
                  instr.parsed.info.mint === selectedMint && 
                  instr.parsed.info.source === senderATA.toBase58() && 
                  (instr.parsed.info.destination === PRESALE_WALLET || instr.parsed.info.destination === receiverATA.toBase58())) {
                const potentialAmount = instr.parsed.type === 'transfer' 
                  ? parseFloat(instr.parsed.info.amount) / 10 ** 6 
                  : parseFloat(instr.parsed.info.tokenAmount.uiAmount);
                if (potentialAmount > 0.002) {
                  amountPaid = potentialAmount;
                  console.log(`Check: Amount paid from ${instr.parsed.type} instruction: ${amountPaid} in transaction ${sig.signature}`);
                  break;
                } else {
                  console.log(`Check: Ignoring small amount ${potentialAmount} from ${instr.parsed.type} instruction in transaction ${sig.signature}`);
                }
              }
            }
          }

          // Check inner instructions for Transfer
          if (meta.innerInstructions && amountPaid === 0) {
            for (const instruction of meta.innerInstructions) {
              for (const instr of instruction.instructions) {
                if (instr.parsed && instr.parsed.type === 'transfer' && 
                    instr.parsed.info.mint === selectedMint && 
                    instr.parsed.info.source === senderATA.toBase58() && 
                    (instr.parsed.info.destination === PRESALE_WALLET || instr.parsed.info.destination === receiverATA.toBase58())) {
                  const potentialAmount = parseFloat(instr.parsed.info.amount) / 10 ** 6;
                  if (potentialAmount > 0.002) {
                    amountPaid = potentialAmount;
                    console.log(`Check: Amount paid from inner instructions: ${amountPaid} in transaction ${sig.signature}`);
                    break;
                  } else {
                    console.log(`Check: Ignoring small amount ${potentialAmount} from inner instructions in transaction ${sig.signature}`);
                  }
                }
              }
              if (amountPaid > 0) break;
            }
          }

          if (amountPaid === 0) {
            console.log(`Check: No valid USDC/USDT transfer found in transaction ${sig.signature}`);
            continue;
          }
        }

        const expectedAmount = parseFloat(transaction.amount);
        const lowerBound = expectedAmount * 0.95;
        const upperBound = expectedAmount * 1.05;
        paymentFound = amountPaid >= lowerBound && amountPaid <= upperBound;
        if (paymentFound && !meta.err) {
          txHashResult = sig.signature;
          break;
        } else {
          console.log(`Check: Amount mismatch in transaction ${sig.signature}. Expected range [${lowerBound}, ${upperBound}], got ${amountPaid}`);
          continue;
        }
      }

      if (paymentFound) {
        const stageRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/stages`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const stage = stageRes.data.find(s => s.stage_id === transaction.stage_id);
        const pricePerToken = stage?.price_per_token || 0.005;
        if (pricePerToken <= 0) {
          console.error(`Invalid pricePerToken: ${pricePerToken} for stage ${transaction.stage_id}`);
          setError(`Invalid price per token for stage ${transaction.stage_id}`);
          return { success: false };
        }
        const tokensPurchased = amountPaid / pricePerToken;

        const updateData = {
          transaction_status: 'success',
          transaction_hash: txHashResult,
          tokens_purchased: tokensPurchased,
          amount: amountPaid,
          payment_coin: actualPaymentCoin, // Update to SOL if detected
        };
        await axios.put(`${process.env.REACT_APP_API_URL}/api/purchases/${transaction.purchase_id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log(`Updated purchase ${transaction.purchase_id} to success with amount ${amountPaid} and payment coin ${actualPaymentCoin}`);

        setTransactions(prev => prev.map(t => t.purchase_id === transaction.purchase_id ? { ...t, ...updateData } : t));
        fetchTransactions();
        return { success: true };
      } else {
        setError('No valid payment detected for the remaining pending transactions. Please enter the transaction hash and click "Confirm With Hash".');
        return { success: false };
      }
    } catch (err) {
      console.error('Check transaction error:', err);
      setError('Error confirming payment: ' + (err.response?.data?.message || err.message));
      return { success: false };
    }
  }, [navigate, solPrice, fetchSolPrice, connection, fetchTransactions]);

  const handleCheckPayments = async () => {
    setLoading(true);
    setError('');

    try {
      const pendingTxs = transactions.filter(tx => tx.transaction_status.toLowerCase() === 'pending');
      if (pendingTxs.length === 0) {
        setError('No pending transactions to check.');
        setLoading(false);
        return;
      }

      for (const transaction of pendingTxs) {
        const result = await checkTransaction(transaction);
        if (!result.success) {
          setShowManualInputs(prev => ({ ...prev, [transaction.purchase_id]: true }));
          setHideCheckSignature(prev => ({ ...prev, [transaction.purchase_id]: true })); // Hide "Check Signature" button
        }
      }
    } catch (err) {
      console.error('Check payments error:', err);
      setError('Error checking payments: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleManualConfirm = async (transaction) => {
    const txHash = manualTxHashes[transaction.purchase_id] || '';
    if (!txHash.trim()) {
      setError('Please enter a transaction hash for this transaction.');
      return;
    }

    await checkTransaction(transaction, txHash, true);
  };

  const toggleManualInput = (purchaseId) => {
    setShowManualInputs(prev => ({
      ...prev,
      [purchaseId]: !prev[purchaseId],
    }));
    setHideCheckSignature(prev => ({
      ...prev,
      [purchaseId]: true, // Hide "Check Signature" button after clicking
    }));
  };

  const formatNumber = (num) => {
    return parseFloat(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="page-container">
      <Header />
      <section className="content-container container my-4">
        <h2 className="text-center mb-4 text-white">Transaction Details</h2>
        {error && <div className="text-danger text-center mb-4">{error}</div>}
        {loading && <div className="text-center mb-4 text-white">Loading...</div>}
        <div style={{ marginBottom: '20px' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCheckPayments}
            disabled={loading || transactions.filter(tx => tx.transaction_status.toLowerCase() === 'pending').length === 0}
          >
            {loading ? 'Checking...' : 'Check Payments'}
          </Button>
        </div>
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className="hide-on-mobile">Trn. No.</TableCell>
                <TableCell>Stage</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell className="hide-on-mobile">Paid with</TableCell>
                <TableCell>Status</TableCell>
                <TableCell className="hide-on-mobile">TRN. Hash</TableCell>
                <TableCell className="hide-on-mobile">Address</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.length === 0 ? (
                <TableRow><TableCell colSpan="9" align="center">No transactions found</TableCell></TableRow>
              ) : (
                transactions.map(tx => (
                  <TableRow key={tx.purchase_id}>
                    <TableCell className="hide-on-mobile">{tx.purchase_id}</TableCell>
                    <TableCell>{tx.stage_id}</TableCell>
                    <TableCell>{formatNumber(tx.tokens_purchased)}</TableCell>
                    <TableCell>{formatNumber(tx.amount)}</TableCell>
                    <TableCell className="hide-on-mobile">{tx.payment_coin}</TableCell>
                    <TableCell>{tx.transaction_status}</TableCell>
                    <TableCell className="hide-on-mobile">
                      {tx.transaction_hash ? (
                        <a href={`https://solscan.io/tx/${tx.transaction_hash}`} target="_blank" rel="noopener noreferrer">
                          {tx.transaction_hash.slice(0, 8)}...
                        </a>
                      ) : 'N/A'}
                    </TableCell>
                    <TableCell className="hide-on-mobile">{tx.wallet_address.slice(0, 8)}...</TableCell>
                    <TableCell>
                      {tx.transaction_status.toLowerCase() === 'pending' && (
                        <>
                          {!hideCheckSignature[tx.purchase_id] && (
                            <Button
                              variant="contained"
                              color="secondary"
                              onClick={() => toggleManualInput(tx.purchase_id)}
                              disabled={loading}
                            >
                              Check Signature
                            </Button>
                          )}
                          {showManualInputs[tx.purchase_id] && (
                            <div style={{ marginTop: '10px' }}>
                              <input
                                type="text"
                                placeholder="Enter Transaction Hash"
                                value={manualTxHashes[tx.purchase_id] || ''}
                                onChange={(e) => setManualTxHashes(prev => ({
                                  ...prev,
                                  [tx.purchase_id]: e.target.value,
                                }))}
                                style={{ marginBottom: '10px', padding: '5px', width: '100%' }}
                              />
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={() => handleManualConfirm(tx)}
                                disabled={loading}
                              >
                                {loading ? 'Verifying...' : 'Confirm with Hash'}
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      </section>
      <Footer />
    </div>
  );
};

export default TransactionDetail;
